import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Upload } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const DashboardProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState({
    username: "",
    bio: "",
    avatar_url: ""
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }
    loadProfile(session.user.id);
  };

  const loadProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      toast.error("Error loading profile");
    } else if (data) {
      setProfile({
        username: data.username || "",
        bio: data.bio || "",
        avatar_url: data.avatar_url || ""
      });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate("/auth");
      return;
    }

    const { error } = await supabase
      .from("profiles")
      .update({
        username: profile.username,
        bio: profile.bio,
        avatar_url: profile.avatar_url
      })
      .eq("id", session.user.id);

    if (error) {
      toast.error("Error updating profile");
    } else {
      toast.success("Profile updated successfully");
    }
    setSaving(false);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    setUploading(true);
    const fileExt = file.name.split('.').pop();
    const fileName = `${session.user.id}/${Math.random()}.${fileExt}`;

    const { error: uploadError, data } = await supabase.storage
      .from("avatars")
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      toast.error("Error uploading avatar");
      setUploading(false);
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("avatars")
      .getPublicUrl(fileName);

    setProfile({ ...profile, avatar_url: publicUrl });
    setUploading(false);
    toast.success("Avatar uploaded successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Bold Vibrant Background with Mesh Gradient */}
      <div 
        className="absolute inset-0 -z-10 animate-gradient"
        style={{ 
          background: 'var(--gradient-mesh)',
          backgroundColor: 'hsl(var(--background))'
        }}
      />
      {/* Additional animated overlay */}
      <div className="absolute inset-0 -z-10 opacity-30">
        <div className="absolute top-0 -left-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-6 hover:bg-background/80 backdrop-blur-sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <div className="space-y-6 animate-fade-in">
          {/* Profile Header Card */}
          <Card className="border-2 backdrop-blur-sm bg-card/80 shadow-[var(--shadow-elevated)] hover:shadow-[var(--shadow-glow)] transition-all duration-300">
            <CardContent className="pt-8">
              {/* Avatar Upload */}
              <div className="flex flex-col items-center gap-6 mb-8">
                <div className="relative group">
                  <div 
                    className="w-40 h-40 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center overflow-hidden shadow-[var(--shadow-glow)] ring-4 ring-background transition-transform duration-300 group-hover:scale-105"
                  >
                    {profile.avatar_url ? (
                      <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-6xl text-white font-bold">
                        {profile.username?.[0]?.toUpperCase() || "U"}
                      </span>
                    )}
                  </div>
                  {/* Glow effect */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 blur-2xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold">{profile.username || "Your Name"}</h2>
                  <label>
                    <Button 
                      variant="outline" 
                      disabled={uploading} 
                      asChild
                      className="bg-background/50 backdrop-blur-sm hover:bg-background/80"
                    >
                      <span className="cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? "Uploading..." : "Change Avatar"}
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Details Card */}
          <Card className="border-2 backdrop-blur-sm bg-card/80 shadow-[var(--shadow-card)]">
            <CardHeader>
              <CardTitle className="text-2xl">Profile Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Username */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Username
                </label>
                <Input
                  value={profile.username}
                  onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                  placeholder="Enter your username"
                  className="bg-background/50 backdrop-blur-sm border-2 focus:ring-2 focus:ring-primary/20 transition-all"
                />
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Bio
                </label>
                <Textarea
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  placeholder="Tell us about yourself, your goals, and what you're working on..."
                  rows={5}
                  className="bg-background/50 backdrop-blur-sm border-2 focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  {profile.bio?.length || 0} characters
                </p>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full shadow-[var(--shadow-glow)] hover:shadow-[var(--shadow-glow)] hover:scale-[1.02] transition-all duration-300"
                size="lg"
              >
                {saving ? "Saving..." : "Save Profile"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardProfile;
